function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useCallbackByKey, useMemoByKey } from '@guryou/hooks';
import { stateError, stateIsEmpty, stateIsLoaded, stateIsLoading, stateIsNotInitialized } from '@guryou/redux';
import { generateHashCode } from '@guryou/utils';
import { Paper } from '@material-ui/core';
import PropTypes from 'prop-types';
import { CenterMediumContent, FlexRow } from '../content';
import { FullScreenSpinner } from '../spinner';
import Table from '../Table';
import { CardBody, EmptyPage, Filters, Modals, TableCellExpandBody, TableRowBody, ToolbarActions } from './Components';
import ContainerHeader from './ContainerHeader';
import Pagination from './Pagination';
import { useProviderSearch } from './ProviderSearchContext';
import { tableRowToColumns } from './utils';
export const VIEW_TYPES = {
  CARDS: 1,
  ROWS: 2,
  TABLE: 3
};
const MultipleItemsContainer = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    children,
    customClass,
    customKey,
    data,
    downloadFilename,
    entity,
    initialFilter = {},
    noHeader,
    onlyTitle,
    pageSize = 8,
    disableSort,
    showDownloadTool = false,
    showPrintTool = false,
    showAiTool = false,
    onAiClick,
    singleExpandedRow = true,
    title,
    titleAndToolbars = false,
    version,
    extraButton,
    rowsPerPageOptions,
    passingRef = false,
    ComponentToPrint = null,
    lazyPrint = false,
    paidPackage = false,
    hideTitle = false,
    skeleton = null,
    stripeResolver = null
  } = props;
  const [filteredItems, setFilteredItems] = useState(data);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [localFilter, setLocalFilter] = useState(initialFilter);

  // Lazy print: ComponentToPrint is only mounted for the duration of a print, instead of
  // always sitting hidden in the DOM re-rendering the full (unpaginated) dataset on every
  // filter/search change. onBeforeGetContent mounts it and waits for the ref to attach
  // before react-to-print reads `content()`.
  const [isPrinting, setIsPrinting] = useState(false);
  const printReadyResolveRef = useRef(null);
  const handleLazyPrint = useReactToPrint({
    content: () => ref.current,
    removeAfterPrint: true,
    onBeforeGetContent: () => new Promise(resolve => {
      printReadyResolveRef.current = resolve;
      setIsPrinting(true);
    }),
    onAfterPrint: () => setIsPrinting(false),
    onPrintError: () => setIsPrinting(false)
  });
  useEffect(() => {
    if (isPrinting && printReadyResolveRef.current) {
      const resolve = printReadyResolveRef.current;
      printReadyResolveRef.current = null;
      resolve();
    }
  }, [isPrinting]);

  // When the search term is lifted to the L1 chrome (deal provider), merge it into the
  // filter input here, since it no longer lives in the form. No-op otherwise.
  const {
    active: searchLifted,
    term: searchTerm
  } = useProviderSearch();
  const mergeSearch = input => searchLifted ? {
    ...(input || {}),
    search: searchTerm
  } : input;

  // Active filter count (excludes search) for the L2 "Filtri" badge.
  const activeFilterCount = Object.entries(localFilter || {}).filter(([k, v]) => k !== 'search' && v !== null && v !== undefined && v !== '' && v !== 'all').length;
  const _children = React.Children.toArray(children);
  const filtersContent = _children.find(x => x.type.name === Filters.name);
  const filterChildren = filtersContent?.props?.children;
  const modalsContent = _children.find(x => x.type.name === Modals.name);
  const toolbarActionsContent = _children.find(x => x.type.name === ToolbarActions.name);
  const cardBody = _children.find(x => x.type.name === CardBody.name);
  const tableRow = _children.find(x => x.type.name === TableRowBody.name);
  const emptyPage = _children.find(x => x.type.name === EmptyPage.name);
  const onFilterChange = filtersContent?.props?.onChange;
  const onFilterSubmit = filtersContent?.props?.onSubmit;
  const normalizedEntity = entity?.replace('PROVIDER_', '');
  const onFormChangeCallback = useCallbackByKey(input => {
    disableSort && disableSort(true);
    setLocalFilter(input);
    const items = onFilterChange(mergeSearch(input), data);
    setFilteredItems(items);
    setCurrentOffset(0);
  }, [data, disableSort, searchTerm, searchLifted]);
  const onFormSubmitCallback = useCallbackByKey(input => {
    disableSort && disableSort(true);
    setLocalFilter(input);
    onFilterSubmit(input);
    setFilteredItems([]);
  }, [disableSort]);
  const filterFormProps = {};
  if (onFilterSubmit) {
    filterFormProps.onSubmit = onFormSubmitCallback;
  } else {
    filterFormProps.onChange = onFormChangeCallback;
  }
  useEffect(() => {
    let items = onFilterChange ? onFilterChange(mergeSearch(localFilter), data) : data;
    if (!disableSort && items) {
      items = items.sort((a, b) => a.orderIndex - b.orderIndex);
    } else if (disableSort && items) {
      items = items.sort((a, b) => b.id - a.id);
    }
    setFilteredItems(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, localFilter, onFilterChange, disableSort, searchTerm, searchLifted]);
  if ((stateIsLoading(props) || stateIsNotInitialized(props)) && !stateIsLoaded(props)) {
    return skeleton || /*#__PURE__*/React.createElement(FullScreenSpinner, null);
  }
  if (emptyPage && (stateIsEmpty(props) || stateError(props))) {
    return /*#__PURE__*/React.createElement(CenterMediumContent, {
      className: "text-center"
    }, children.filter(x => x.type.name === EmptyPage.name), modalsContent);
  }
  let mainContent = null;
  if (cardBody) {
    const cardsContent = filteredItems?.slice(currentOffset, currentOffset + pageSize)?.map((item, idx) => cardBody.props.render(item, idx));
    mainContent = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FlexRow, null, cardsContent), /*#__PURE__*/React.createElement(Pagination, {
      limit: pageSize,
      total: data ? (filteredItems || data).length : [],
      offset: currentOffset,
      onClick: (_, offset) => setCurrentOffset(offset)
    }));
  } else {
    const columns = tableRowToColumns(tableRow);
    const renderExpandableRow = tableRow.props.children.find(x => x.type.name === TableCellExpandBody.name);
    mainContent = /*#__PURE__*/React.createElement(Table, {
      ref: passingRef ? null : ref,
      key: generateHashCode({
        localFilter,
        filteredItems
      }),
      customClass: customClass,
      showDownloadTool: showDownloadTool,
      showPrintTool: showPrintTool,
      customKey: customKey,
      data: filteredItems || [],
      columns: columns,
      toolbars: null,
      renderExpandableRow: renderExpandableRow && renderExpandableRow.props.render,
      downloadFilename: downloadFilename,
      singleExpandedRow: singleExpandedRow,
      setDisableSort: disableSort,
      rowsPerPageOptions: rowsPerPageOptions,
      stripeResolver: stripeResolver
    });
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ContainerHeader, _extends({
    ref: ref,
    key: `${title}_${entity}`,
    titleAndToolbars: titleAndToolbars,
    noHeader: noHeader,
    onlyTitle: onlyTitle,
    title: title,
    toolbarActionsContent: toolbarActionsContent,
    entity: normalizedEntity,
    showDownloadTool: showDownloadTool,
    showAiTool: showAiTool,
    onAiClick: onAiClick,
    extraButton: extraButton,
    paidPackage: paidPackage,
    hideTitle: hideTitle,
    filterCount: activeFilterCount,
    onPrintClick: lazyPrint ? handleLazyPrint : undefined
  }, filterFormProps), filterChildren), /*#__PURE__*/React.createElement(Wrapper, {
    cardBody: cardBody
  }, mainContent, modalsContent, ComponentToPrint && (!lazyPrint || isPrinting) && /*#__PURE__*/React.cloneElement(ComponentToPrint, {
    printData: filteredItems
  })));
});
const Wrapper = ({
  children,
  cardBody = false
}) => /*#__PURE__*/React.createElement(React.Fragment, null, cardBody ? /*#__PURE__*/React.createElement(React.Fragment, null, children) : /*#__PURE__*/React.createElement(Paper, {
  elevation: 2
}, children));
MultipleItemsContainer.propTypes = {
  pageSize: PropTypes.number,
  viewType: PropTypes.number,
  status: PropTypes.number,
  entity: PropTypes.string,
  data: PropTypes.array,
  stripeResolver: PropTypes.func,
  onAiClick: PropTypes.func,
  singleExpandedRow: PropTypes.bool,
  columns: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    label: PropTypes.string,
    options: PropTypes.object
  }))
};
export default MultipleItemsContainer;